import * as React from 'react';
const {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} = React;
import { PlaybackButton } from '@/common/components/ui/LibraryPrimitives';
import { useLazyWavesurfer } from '@/hooks/useLazyWavesurfer';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Popover,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { type GetCommentsQueryData, type WaveForm } from '@/generated/graphql';
import {
  decodeWaveformPeaks,
  formatTimeMs,
} from '@/common/utils/audioPlayerUtils';
import { AuthorAvatar, authorName } from './AuthorAvatar';

type Thread = GetCommentsQueryData['comments'][number];

export interface ReviewWaveformHandle {
  seekToMs: (ms: number) => void;
}

interface ReviewWaveformProps {
  waveForm: WaveForm;
  projectId: string;
  fileId: string;
  /** Top-level threads; only open (unresolved) ones render markers. */
  threads: Thread[];
  onTimeChange: (ms: number) => void;
  onMarkerClick: (threadId: string) => void;
  /** Playhead to start at (e.g. the banner's position). */
  initialTimeMs?: number;
}

export const MARKER_LANES = 3;
export const MARKER_MIN_GAP_PX = 24;
const LANE_HEIGHT = 22;

export interface MarkerLayout {
  placed: Array<{ id: string; lane: number; px: number }>;
  /** Clusters of ids that did not fit in any lane (4th+ collision). */
  overflow: Array<{ ids: string[]; px: number }>;
}

/**
 * Greedy lane assignment: a marker takes the first lane whose last marker is
 * at least `minGap` px away; if none, it joins an overflow cluster. With an
 * unknown (<= 0) width everything stays in a single lane.
 */
export function layoutMarkers(
  items: Array<{ id: string; px: number }>,
  maxLanes = MARKER_LANES,
  minGap = MARKER_MIN_GAP_PX
): MarkerLayout {
  const sorted = [...items].sort((a, b) => a.px - b.px);
  const lastPx: number[] = [];
  const placed: MarkerLayout['placed'] = [];
  const overflow: MarkerLayout['overflow'] = [];
  let lastOverflowPx = -Infinity;
  for (const it of sorted) {
    let lane = -1;
    for (let l = 0; l < maxLanes; l++) {
      if (lastPx[l] === undefined || it.px - lastPx[l] >= minGap) {
        lane = l;
        break;
      }
    }
    if (lane >= 0) {
      lastPx[lane] = it.px;
      placed.push({ id: it.id, lane, px: it.px });
    } else if (overflow.length && it.px - lastOverflowPx < minGap) {
      overflow[overflow.length - 1].ids.push(it.id);
      lastOverflowPx = it.px;
    } else {
      overflow.push({ ids: [it.id], px: it.px });
      lastOverflowPx = it.px;
    }
  }
  return { placed, overflow };
}

/** Tracks an element's content width (0 until measured / without ResizeObserver). */
export function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number') setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export const CommentMarkers = React.memo(function CommentMarkers({
  threads,
  durationMs,
  width,
  onMarkerClick,
}: {
  threads: Thread[];
  durationMs: number;
  width: number;
  onMarkerClick: (threadId: string) => void;
}) {
  const theme = useTheme();
  const [cluster, setCluster] = useState<{
    anchor: HTMLElement;
    ids: string[];
  } | null>(null);

  const byId = useMemo(
    () => new Map(threads.map((t) => [t._id, t])),
    [threads]
  );
  const layout = useMemo(() => {
    const items = threads.map((t) => ({
      id: t._id,
      px:
        Math.min(1, Math.max(0, t.anchor.atMs / durationMs)) *
        (width > 0 ? width : 0),
    }));
    return width > 0 ? layoutMarkers(items) : layoutMarkers(items, 1, 0);
  }, [threads, durationMs, width]);

  const leftFor = (px: number, id: string) =>
    width > 0
      ? `${px}px`
      : `${Math.min(100, Math.max(0, ((byId.get(id)?.anchor.atMs ?? 0) / durationMs) * 100))}%`;

  const focusRing = {
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  };
  const bg = theme.palette.warning.main;
  const fg = theme.palette.getContrastText(bg);

  return (
    <Box
      data-cy="review-marker-layer"
      sx={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}
    >
      {layout.placed.map(({ id, lane, px }) => {
        const t = byId.get(id)!;
        const time = formatTimeMs(t.anchor.atMs);
        const name = authorName(t.author);
        const firstLine = t.body.split('\n')[0];
        return (
          <Tooltip key={id} title={`${time} · ${firstLine}`} placement="top">
            <ButtonBase
              data-cy="review-comment-marker"
              aria-label={`Comment at ${time} by ${name}`}
              onClick={() => onMarkerClick(id)}
              focusRipple
              sx={{
                position: 'absolute',
                left: leftFor(px, id),
                top: lane * LANE_HEIGHT,
                width: 24,
                height: 24,
                borderRadius: '50%',
                transform: 'translateX(-50%)',
                pointerEvents: 'auto',
                ...focusRing,
              }}
            >
              <AuthorAvatar author={t.author} sx={{ bgcolor: bg, color: fg }} />
            </ButtonBase>
          </Tooltip>
        );
      })}
      {layout.overflow.map(({ ids, px }) => (
        <ButtonBase
          key={ids[0]}
          data-cy="review-comment-cluster"
          aria-label={`${ids.length} more ${ids.length === 1 ? 'comment' : 'comments'}, show list`}
          onClick={(e) => setCluster({ anchor: e.currentTarget, ids })}
          sx={{
            position: 'absolute',
            left: `${px}px`,
            top: MARKER_LANES * LANE_HEIGHT,
            minWidth: 24,
            height: 24,
            px: 0.5,
            borderRadius: 12,
            transform: 'translateX(-50%)',
            pointerEvents: 'auto',
            fontSize: 11,
            fontWeight: 600,
            bgcolor: bg,
            color: fg,
            ...focusRing,
          }}
        >
          +{ids.length}
        </ButtonBase>
      ))}
      <Popover
        open={!!cluster}
        anchorEl={cluster?.anchor}
        onClose={() => setCluster(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 1, maxWidth: 280 }}>
          {cluster?.ids.map((id) => {
            const t = byId.get(id);
            if (!t) return null;
            return (
              <ButtonBase
                key={id}
                data-cy="review-comment-cluster-item"
                onClick={() => {
                  onMarkerClick(id);
                  setCluster(null);
                }}
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'flex-start',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  textAlign: 'left',
                  ...focusRing,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatTimeMs(t.anchor.atMs)}
                </Typography>
                <Typography variant="body2" noWrap>
                  {authorName(t.author)}: {t.body.split('\n')[0]}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
});

export const ReviewWaveform = forwardRef<
  ReviewWaveformHandle,
  ReviewWaveformProps
>(function ReviewWaveform(
  {
    waveForm,
    projectId,
    fileId,
    threads,
    onTimeChange,
    onMarkerClick,
    initialTimeMs,
  },
  ref
) {
  const theme = useTheme();
  const peaks = useMemo(
    () => decodeWaveformPeaks(waveForm.peaks),
    [waveForm.peaks]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const pendingSeekMsRef = useRef<number | null>(
    initialTimeMs ? initialTimeMs : null
  );
  const applyPendingSeek = useCallback(
    (ws: { setTime: (seconds: number) => void }) => {
      if (pendingSeekMsRef.current !== null) {
        ws.setTime(pendingSeekMsRef.current / 1000);
        pendingSeekMsRef.current = null;
      }
    },
    []
  );
  const {
    wavesurfer,
    isPlaying,
    currentTime,
    audioSourceURL,
    isLoadingAudioSource,
    readyRef,
    onPlayPause,
  } = useLazyWavesurfer({
    projectId,
    fileId,
    containerRef,
    onAudioReady: applyPendingSeek,
    options: {
      height: 96,
      peaks,
      duration: waveForm.duration,
      waveColor: alpha(theme.palette.text.secondary, 0.35),
      progressColor: theme.palette.primary.main,
      cursorColor: alpha(theme.palette.primary.light, 0.8),
      cursorWidth: 1,
      dragToSeek: true,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
    },
  });

  // Report the playhead to the parent (composer anchors new comments to it).
  useEffect(() => {
    // Keep the initial playhead until the first seek has been applied.
    if (currentTime === 0 && pendingSeekMsRef.current !== null) return;
    onTimeChange(currentTime * 1000);
  }, [currentTime, onTimeChange]);

  // Same pre-load path as seekToMs: move the cursor from peaks now, and the
  // pending value is re-applied when the audio becomes ready.
  const initialTimeRef = useRef(initialTimeMs);
  useEffect(() => {
    if (!wavesurfer || !initialTimeRef.current) return;
    wavesurfer.setTime(initialTimeRef.current / 1000);
    initialTimeRef.current = undefined;
  }, [wavesurfer]);

  // Clicking the waveform before audio is loaded does not move the media
  // element, so report the interaction time directly.
  useEffect(() => {
    if (!wavesurfer) return;
    const onInteraction = (newTime: number) => {
      if (!audioSourceURL || !readyRef.current) {
        pendingSeekMsRef.current = newTime * 1000;
      }
      onTimeChange(newTime * 1000);
    };
    wavesurfer.on('interaction', onInteraction);
    return () => {
      wavesurfer.un('interaction', onInteraction);
    };
  }, [wavesurfer, audioSourceURL, readyRef, onTimeChange]);

  useImperativeHandle(
    ref,
    () => ({
      seekToMs: (ms: number) => {
        if (!wavesurfer) return;
        // Always move the visual cursor/time (wavesurfer renders from peaks +
        // duration without media); keep the value so it is re-applied once
        // when the audio becomes ready.
        if (!audioSourceURL || !readyRef.current) pendingSeekMsRef.current = ms;
        wavesurfer.setTime(ms / 1000);
        onTimeChange(ms);
      },
    }),
    [wavesurfer, audioSourceURL, readyRef, onTimeChange]
  );

  const { ref: laneRef, width } = useElementWidth<HTMLDivElement>();

  const openThreads = useMemo(
    () =>
      waveForm.duration > 0
        ? threads.filter((t) => !t.resolvedAt && t.anchor)
        : [],
    [threads, waveForm.duration]
  );

  return (
    <Box
      data-cy="review-waveform"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}
    >
      <PlaybackButton
        playing={isPlaying}
        size="small"
        disabled={isLoadingAudioSource}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        onClick={() => void onPlayPause()}
      >
        {isLoadingAudioSource ? (
          <CircularProgress size={13} color="inherit" />
        ) : isPlaying ? (
          <PauseIcon sx={{ fontSize: 16 }} />
        ) : (
          <PlayArrowIcon sx={{ fontSize: 16 }} />
        )}
      </PlaybackButton>
      <Box ref={laneRef} sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <Box
          data-cy="review-waveform-host"
          sx={{ position: 'relative', zIndex: 0 }}
        >
          <div ref={containerRef} style={{ width: '100%' }} />
        </Box>
        <CommentMarkers
          threads={openThreads}
          durationMs={waveForm.duration * 1000}
          width={width}
          onMarkerClick={onMarkerClick}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          minWidth: 40,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          color: 'text.secondary',
        }}
      >
        {formatTimeMs(currentTime * 1000)}
      </Typography>
    </Box>
  );
});
